import * as React from "react";
import {
  Archive,
  Factory,
  Mail,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  RotateCcw,
  ShieldAlert,
  UserRound,
  Wallet,
} from "lucide-react";
import { Link, useLocation, useParams, useSearchParams } from "react-router";
import { toast } from "react-toastify";
import { useToggle } from "react-use";

import {
  useArchiveSupplierMutation,
  useGetSupplierFamiliesQuery,
  useGetSupplierContractValuesQuery,
  useGetSupplierQuery,
  useRestoreSupplierMutation,
} from "@/api/suppliers";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { Badge } from "@/components/ui/badge";
import { Toggle } from "@/components/feature/layout/toggle";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import type { DirectoryUser } from "@/types/user";

import { AddContractFamilyDialog } from "./components/add-contract-family-dialog";
import {
  ContractFamilyCategory,
  ContractFamilyExpiry,
  ContractValue,
  GbpEquivalent,
  GbpTotal,
} from "./components/contract-family-enrichment";
import { formatContractValue } from "./components/contract-value-format";
import { EditSupplierContactsDialog } from "./components/edit-supplier-contacts-dialog";
import { EditSupplierDialog } from "./components/edit-supplier-dialog";
import { SupplierCategories } from "./components/supplier-categories";
import { SupplierFamilyPanel } from "./components/supplier-family-panel";

export const SupplierDetailPage: React.FC = () => {
  const { supplierId = "" } = useParams<{ supplierId: string }>();
  const { hash } = useLocation();
  const [searchParams] = useSearchParams();
  const initialFamilyId = searchParams.get("familyId")?.trim() || undefined;
  const contractFamiliesRef = React.useRef<HTMLElement>(null);
  const didScrollToContractFamiliesRef = React.useRef(false);
  const user = useAuthenticatedUser();
  const [familyOffset, setFamilyOffset] = React.useState(0);
  const [pollFamilyDescriptions, setPollFamilyDescriptions] = React.useState(false);
  const [includeAllContractValues, toggleIncludeAllContractValues] = useToggle(false);
  const {
    data: supplier,
    isLoading,
    isError,
    refetch: refetchSupplier,
  } = useGetSupplierQuery(supplierId, { skip: !supplierId });
  const {
    data: families,
    isLoading: familiesLoading,
    isError: familiesError,
    refetch: refetchFamilies,
  } = useGetSupplierFamiliesQuery(
    { supplierId, params: { offset: familyOffset, limit: 50 } },
    {
      pollingInterval: pollFamilyDescriptions ? 5000 : 0,
      skip: !supplierId,
      skipPollingIfUnfocused: true,
    },
  );
  const {
    data: contractValues,
    isLoading: contractValuesLoading,
    isError: contractValuesError,
  } = useGetSupplierContractValuesQuery({ supplierId, includeAll: includeAllContractValues }, { skip: !supplierId });
  const hasPendingFamilyDescription =
    families?.items.some(family => family.description === null && family.accessible_document_count > 0) ?? false;
  const familyTotal = families?.total;
  const familyLimit = families?.limit;

  React.useEffect(() => {
    setPollFamilyDescriptions(hasPendingFamilyDescription);
  }, [hasPendingFamilyDescription]);

  React.useEffect(() => {
    if (hash === "#contract-families" && families && !didScrollToContractFamiliesRef.current) {
      didScrollToContractFamiliesRef.current = true;
      contractFamiliesRef.current?.scrollIntoView?.({ block: "start" });
    }
  }, [families, hash]);

  React.useEffect(() => {
    if (familyTotal === undefined || familyLimit === undefined || familyOffset < familyTotal) return;
    const lastPageOffset = Math.max(0, Math.ceil(familyTotal / familyLimit) - 1) * familyLimit;
    setFamilyOffset(lastPageOffset);
  }, [familyLimit, familyOffset, familyTotal]);
  const ownerIds = React.useMemo(() => {
    const ids = [supplier?.owner_user_id, ...(families?.items.map(family => family.owner_user_id) ?? [])];
    return Array.from(new Set(ids.filter((id): id is string => Boolean(id))));
  }, [families?.items, supplier?.owner_user_id]);
  const { data: owners = [] } = useGetDirectoryUsersQuery(ownerIds, { skip: ownerIds.length === 0 });
  const ownersById = React.useMemo<ReadonlyMap<string, DirectoryUser>>(
    () => new Map(owners.map(owner => [owner.id, owner])),
    [owners],
  );
  const [archiveSupplier, { isLoading: archiveLoading }] = useArchiveSupplierMutation();
  const [restoreSupplier, { isLoading: restoreLoading }] = useRestoreSupplierMutation();
  const [editOpen, toggleEditOpen] = useToggle(false);
  const [contactsOpen, toggleContactsOpen] = useToggle(false);
  const [addFamilyOpen, toggleAddFamilyOpen] = useToggle(false);
  const [lifecycleOpen, toggleLifecycleOpen] = useToggle(false);

  const canAdmin =
    user.pfRoles.includes("superadmin") ||
    (user.features.includes("user_admin") && user.pfRoles.includes("organization_admin"));

  const refreshFamilies = () => {
    refetchFamilies();
    refetchSupplier();
  };

  const updateLifecycle = async () => {
    if (!supplier) return;
    try {
      if (supplier.status === "active") await archiveSupplier(supplier.id).unwrap();
      else await restoreSupplier(supplier.id).unwrap();
      toast.success(supplier.status === "active" ? "Supplier archived" : "Supplier restored");
      toggleLifecycleOpen(false);
    } catch {
      toast.error("Supplier status could not be changed");
    }
  };

  if (isLoading) return <main className="p-8 text-sm text-slate-500">Loading Supplier…</main>;
  if (isError || !supplier) {
    return (
      <main className="p-8">
        <h1 className="text-xl font-semibold text-slate-950">Supplier not found</h1>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/suppliers">Back to Suppliers</Link>
        </Button>
      </main>
    );
  }

  const owner = supplier.owner_user_id ? ownersById.get(supplier.owner_user_id) : undefined;
  const ownerName = owner
    ? [owner.name, owner.surname].filter(Boolean).join(" ") || owner.email
    : supplier.owner_user_id
      ? "Unknown user"
      : "Unassigned";
  const active = supplier.status === "active";
  const lifecycleLoading = archiveLoading || restoreLoading;

  return (
    <main
      className="-mx-4 -my-8 min-h-screen bg-[#f7f3ee] px-5 py-6 sm:-mx-6 lg:-mx-8 lg:px-8 lg:py-7"
      aria-label="Supplier detail workspace"
    >
      <div className="mx-auto max-w-7xl">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/suppliers">Suppliers</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{supplier.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <header className="mt-5 flex flex-col gap-5 border-l-[5px] border-[#c9b39c] pl-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <span className="flex size-11 flex-shrink-0 items-center justify-center rounded-md bg-[#07142d] text-white">
              <Factory className="size-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="truncate text-3xl font-bold leading-tight text-[#111b35]">{supplier.name}</h1>
                <Badge variant={active ? "traffic-green" : "secondary"} className="capitalize">
                  {supplier.status}
                </Badge>
              </div>
              <SupplierCategories supplier={supplier} />
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-gray-500">
                <span>Pathfinder ID: {supplier.id}</span>
                {supplier.external_id ? <span>External ID: {supplier.external_id}</span> : null}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" role="group" aria-label="Supplier actions">
            {/* Placeholder for the upcoming supplier risk analysis; disabled until it ships. */}
            <Button type="button" variant="outline" disabled title="Coming soon">
              <ShieldAlert data-icon="inline-start" /> Supplier Risk Analysis
            </Button>
            {active ? (
              <Button type="button" onClick={() => toggleAddFamilyOpen(true)}>
                <Plus data-icon="inline-start" /> Add Contract Family
              </Button>
            ) : null}
            {active || canAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="icon" aria-label="More actions">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={8}
                  className="w-56 [&_[role=menuitem]>svg]:size-4 [&_[role=menuitem]>svg]:shrink-0 [&_[role=menuitem]]:gap-2.5"
                >
                  {active ? (
                    <DropdownMenuGroup>
                      <DropdownMenuItem onSelect={() => toggleEditOpen(true)}>
                        <Pencil />
                        Edit Supplier
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => toggleContactsOpen(true)}>
                        <UserRound />
                        Edit contacts
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  ) : null}
                  {active && canAdmin ? <DropdownMenuSeparator /> : null}
                  {canAdmin ? (
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        className={active ? "text-destructive focus:text-destructive" : undefined}
                        onSelect={() => toggleLifecycleOpen(true)}
                      >
                        {active ? <Archive /> : <RotateCcw />}
                        {active ? "Archive Supplier" : "Restore Supplier"}
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </header>

        {!active ? (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            This Supplier is archived. Details remain readable; restore it to edit metadata, contacts or assignments.
          </div>
        ) : null}

        <section aria-label="Estimated contract values" className="mt-6">
          <div className="mb-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Wallet className="size-4 text-desert-sand-700" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-[#111b35]">Estimated contract values</h2>
            </div>
            <Toggle
              enabled={includeAllContractValues}
              onChange={toggleIncludeAllContractValues}
              label="All"
              size="sm"
            />
          </div>
          {contractValuesLoading ? (
            <div
              className="rounded-lg border border-dashed border-[#ddd5cb] bg-[#fffdfb] px-4 py-5 text-sm text-gray-500"
              role="status"
            >
              Loading estimated contract values…
            </div>
          ) : contractValuesError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-700" role="alert">
              Estimated contract values could not be loaded.
            </div>
          ) : contractValues && (contractValues.currencies.length || contractValues.families.length) ? (
            <article className="rounded-lg border border-[#ddd5cb] bg-[#fffdfb] px-5 py-5 sm:px-7">
              <dl className="grid gap-6 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Annual</dt>
                  <dd className="mt-2 flex flex-wrap gap-x-7 gap-y-2">
                    {contractValues.currencies.map(total => (
                      <span key={total.currency_code} className="text-xl font-bold tabular-nums text-[#111b35]">
                        {formatContractValue(total.estimated_annual_contract_value, total.currency_code)}
                        <span className="ml-2 text-xs font-semibold text-gray-500">{total.currency_code}</span>
                      </span>
                    ))}
                  </dd>
                  <GbpTotal
                    value={contractValues.estimated_annual_contract_value_gbp}
                    contractValues={contractValues}
                  />
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Total</dt>
                  <dd className="mt-2 flex flex-wrap gap-x-7 gap-y-2">
                    {contractValues.currencies.map(total => (
                      <span key={total.currency_code} className="text-xl font-bold tabular-nums text-[#111b35]">
                        {formatContractValue(total.estimated_total_contract_value, total.currency_code)}
                        <span className="ml-2 text-xs font-semibold text-gray-500">{total.currency_code}</span>
                      </span>
                    ))}
                  </dd>
                  <GbpTotal value={contractValues.estimated_total_contract_value_gbp} contractValues={contractValues} />
                </div>
              </dl>
              {contractValues.families.length ? (
                <div className="mt-6 overflow-x-auto border-t border-[#e7e0d9] pt-4">
                  <table className="w-full min-w-[52rem] text-left text-sm">
                    <thead className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                      <tr>
                        <th scope="col" className="pb-2 pr-4">
                          Family
                        </th>
                        <th scope="col" className="pb-2 pr-4">
                          Category
                        </th>
                        <th scope="col" className="pb-2 pr-4">
                          Expiry
                        </th>
                        <th scope="col" className="pb-2 text-right">
                          Annual
                        </th>
                        <th scope="col" className="pb-2 pr-4 text-right">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {contractValues.families.map(family => (
                        <tr key={family.relation_id}>
                          <th scope="row" className="py-1 pr-4 font-medium">
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="truncate">{family.family_name}</span>
                              <Badge
                                aria-label={`${family.family_name} status`}
                                variant={
                                  family.procurement_status === "active"
                                    ? "traffic-green"
                                    : family.procurement_status === "expiring"
                                      ? "traffic-amber"
                                      : family.procurement_status === "expired" ||
                                          family.procurement_status === "archived"
                                        ? "traffic-red"
                                        : "secondary"
                                }
                                className="shrink-0 capitalize"
                              >
                                {family.procurement_status ?? "Not set"}
                              </Badge>
                            </span>
                          </th>
                          <td className="max-w-[16rem] py-1 pr-4">
                            <ContractFamilyCategory category={family.category} />
                          </td>
                          <td className="whitespace-nowrap py-1 pr-4">
                            <ContractFamilyExpiry expiryDate={family.expiry_date} status={family.procurement_status} />
                          </td>
                          <td className="py-1 text-right font-semibold tabular-nums text-[#111b35]">
                            <ContractValue
                              value={family.estimated_annual_contract_value}
                              currencyCode={family.currency_code}
                            />
                            <GbpEquivalent
                              value={family.estimated_annual_contract_value_gbp}
                              currencyCode={family.currency_code}
                            />
                          </td>
                          <td className="py-1 pr-4 text-right font-semibold tabular-nums text-[#111b35]">
                            <ContractValue
                              value={family.estimated_total_contract_value}
                              currencyCode={family.currency_code}
                            />
                            <GbpEquivalent
                              value={family.estimated_total_contract_value_gbp}
                              currencyCode={family.currency_code}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </article>
          ) : (
            <div className="rounded-lg border border-dashed border-[#ddd5cb] bg-[#fffdfb] px-4 py-5 text-sm text-gray-500">
              No estimated values have been added.
            </div>
          )}
        </section>

        <section
          aria-label="Supplier overview"
          className="mt-6 grid overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]/90 md:grid-cols-[minmax(240px,0.8fr)_minmax(0,2fr)]"
        >
          <div className="border-b border-[#e7e0d9] px-6 py-5 md:border-b-0 md:border-r">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
              <UserRound className="size-4 text-desert-sand-700" /> Owner
            </p>
            <p className="mt-3 text-sm font-semibold text-[#111b35]">{ownerName}</p>
            {owner ? (
              <p className="mt-1 text-xs font-medium text-gray-500">{owner.email}</p>
            ) : supplier.owner_user_id ? (
              <p className="mt-1 text-xs font-medium text-gray-500">The referenced owner is unavailable or inactive.</p>
            ) : null}
          </div>
          <div className="px-6 py-5">
            <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Default contact</h2>
            <dl className="mt-3 grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-700">
                <UserRound className="size-4 flex-shrink-0 text-gray-400" />
                <dt className="sr-only">Contact name</dt>
                <dd className="truncate">{supplier.contact_name || "No contact name"}</dd>
              </div>
              <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-700">
                <Mail className="size-4 flex-shrink-0 text-gray-400" />
                <dt className="sr-only">Contact email</dt>
                <dd className="truncate">{supplier.contact_email || "No contact email"}</dd>
              </div>
              <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-700">
                <Phone className="size-4 flex-shrink-0 text-gray-400" />
                <dt className="sr-only">Contact phone</dt>
                <dd className="truncate">{supplier.contact_phone || "No contact phone"}</dd>
              </div>
              {supplier.contact_notes ? (
                <div className="border-t border-[#ebe5de] pt-3 text-sm text-gray-600 sm:col-span-2 lg:col-span-3">
                  <dt className="sr-only">Contact notes</dt>
                  <dd>{supplier.contact_notes}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        </section>

        <section
          id="contract-families"
          ref={contractFamiliesRef}
          className="mt-6 overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]"
          aria-labelledby="contract-families-heading"
        >
          <div className="flex flex-col gap-2 border-b border-[#e4ddd5] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 id="contract-families-heading" className="text-lg font-bold text-[#07142d]">
                Contract Families
              </h2>
              <p className="mt-0.5 text-xs font-medium text-gray-500">
                Only families visible through your document permissions are shown.
              </p>
            </div>
            <Badge variant="outline" className="w-fit bg-white font-medium text-gray-600">
              {families?.total ?? 0} accessible
            </Badge>
          </div>
          {familiesLoading ? (
            <div className="p-10 text-center text-sm font-medium text-gray-500">Loading Contract Families…</div>
          ) : null}
          {familiesError ? (
            <div className="p-10 text-center text-sm text-red-700">Contract Families could not be loaded.</div>
          ) : null}
          {families ? (
            <>
              <SupplierFamilyPanel
                supplierId={supplier.id}
                initialFamilyId={initialFamilyId}
                supplierOwnerId={supplier.owner_user_id}
                supplierStatus={supplier.status}
                families={families.items}
                ownersById={ownersById}
                onChanged={refreshFamilies}
              />
              {families.total > families.limit ? (
                <nav
                  className="flex items-center justify-between border-t border-[#e4ddd5] px-5 py-4"
                  aria-label="Contract Family pages"
                >
                  <Button
                    type="button"
                    variant="outline"
                    disabled={familyOffset === 0}
                    onClick={() => setFamilyOffset(Math.max(0, familyOffset - families.limit))}
                  >
                    Previous families
                  </Button>
                  <span className="text-sm text-slate-500">
                    {families.offset + 1}–{Math.min(families.offset + families.items.length, families.total)} of{" "}
                    {families.total}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={families.offset + families.items.length >= families.total}
                    onClick={() => setFamilyOffset(familyOffset + families.limit)}
                  >
                    Next families
                  </Button>
                </nav>
              ) : null}
            </>
          ) : null}
        </section>
      </div>

      <EditSupplierDialog supplier={supplier} open={editOpen} onOpenChange={toggleEditOpen} />
      <EditSupplierContactsDialog supplierId={supplier.id} open={contactsOpen} onOpenChange={toggleContactsOpen} />
      <AddContractFamilyDialog
        supplierId={supplier.id}
        supplierOwnerId={supplier.owner_user_id}
        open={addFamilyOpen}
        onOpenChange={toggleAddFamilyOpen}
        onAssigned={refreshFamilies}
      />
      <Dialog open={lifecycleOpen} onOpenChange={toggleLifecycleOpen}>
        <DialogContent closeDisabled={lifecycleLoading}>
          <DialogHeader>
            <DialogTitle>{active ? "Archive Supplier" : "Restore Supplier"}</DialogTitle>
            <DialogDescription>
              {active
                ? "Archive this Supplier? New assignments, metadata changes and contact changes will be disabled until it is restored."
                : "Restore this Supplier and re-enable management actions?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => toggleLifecycleOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={active ? "destructive" : "default"}
              onClick={() => void updateLifecycle()}
              disabled={lifecycleLoading}
            >
              {lifecycleLoading ? "Updating…" : active ? "Archive Supplier" : "Restore Supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
};
